import {
    buildFieldSortCriterion,
    buildInteractiveResult,
    buildResultList,
    buildResultsPerPage,
    buildSort,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
    SortOrder,
} from '@coveo/headless';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ProviderCarousel as ProviderCarouselUI } from '@/design-system/molecules/ProviderCarousel';
import { useLocalStorageGeolocation } from '@/hooks/useLocalStorageGeolocation';

const ProviderCarousel = ({
    componentId,
    htmlId,
    title,
    ctaPreText,
    cta,
    dynamicCta,
    coveo,
    coveoQuery,
    randomized,
    isDynamic,
}) => {
    const [localStorageGeolocation] = useLocalStorageGeolocation();
    const hasCoveoQuery = Boolean(coveoQuery);
    const pageSize = randomized ? 25 : 10;
    const [providers, setProviders] = React.useState([]);
    const [showCta, setShowCta] = React.useState(false);

    // Coveo search engine
    const engine = React.useMemo(() => {
        if (coveo && hasCoveoQuery) {
            return getHeadlessEngine(`provider-carousel-${componentId}`, {
                ...coveo,
                search: { searchHub: 'FeaturedDoctors' },
                preprocessRequest: (request, _clientOrigin, metadata) => {
                    if (
                        !metadata ||
                        metadata.method !== 'search' ||
                        randomized ||
                        localStorageGeolocation === null
                    ) {
                        return request;
                    }

                    const body = JSON.parse(request.body);

                    body.queryFunctions = [
                        {
                            fieldName: '@mydistance',
                            function: `dist(@latitude, @longitude, ${
                                localStorageGeolocation.lat ?? 0
                            }, ${localStorageGeolocation.lng ?? 0})`,
                        },
                        {
                            fieldName: '@mydistanceinmiles',
                            function: '@mydistance*0.000621371',
                        },
                    ];

                    request.body = JSON.stringify(body);

                    return request;
                },
                analytics: { analyticsMode: 'legacy', trackingId: 'featured-doctors' },
            });
        }
        return null;
    }, [hasCoveoQuery, coveo, componentId, localStorageGeolocation, randomized]);

    const QueryActionCreators = React.useMemo(
        () => (engine ? loadQueryActions(engine) : null),
        [engine],
    );

    const SearchAnalyticsActionCreators = React.useMemo(
        () => (engine ? loadSearchAnalyticsActions(engine) : null),
        [engine],
    );

    const SearchActionCreators = React.useMemo(
        () => (engine ? loadSearchActions(engine) : null),
        [engine],
    );

    const sortCriteria = React.useMemo(() => {
        let criteria = null;
        if (isDynamic) {
            if (localStorageGeolocation) {
                criteria = [
                    buildFieldSortCriterion('onlinebookingavailable', SortOrder.Descending),
                    buildFieldSortCriterion('mydistanceinmiles', SortOrder.Ascending),
                ];
            } else {
                criteria = buildFieldSortCriterion('onlinebookingavailable', SortOrder.Descending);
            }
        } else {
            if (localStorageGeolocation && !randomized) {
                criteria = [
                    buildFieldSortCriterion('mydistanceinmiles', SortOrder.Ascending),
                    buildFieldSortCriterion('onlinebookingavailable', SortOrder.Descending),
                ];
            }
        }
        return criteria;
    }, [localStorageGeolocation, isDynamic, randomized]);

    const controllerResultsList = React.useMemo(() => {
        if (engine) {
            const controllerResultList = buildResultList(engine, {
                options: {
                    fieldsToInclude: [
                        'entitytype',
                        'filetype',
                        'firstname',
                        'lastname',
                        'fullname',
                        'imageurl',
                        'location',
                        'locationcitystate',
                        'locationcity',
                        'locationstate',
                        'locationzip',
                        'locationphone',
                        'latitude',
                        'longitude',
                        'permanentid',
                        'affiliationrank',
                        'providertype',
                        'providernpi',
                        'specialties',
                        'primaryspecialty',
                        'overallrating',
                        'acceptingnewpatients',
                        'onlinebookingavailable',
                    ],
                },
            });

            buildResultsPerPage(engine, {
                initialState: { numberOfResults: pageSize },
            });

            if (sortCriteria) {
                buildSort(engine, {
                    initialState: {
                        criterion: sortCriteria,
                    },
                });
            }
            return controllerResultList;
        }
        return null;
    }, [engine, pageSize, sortCriteria]);

    const consolidatedCta = React.useMemo(
        () => ({
            ...dynamicCta,
            ...cta,
            href: cta?.href || dynamicCta?.href,
            label: cta?.label || dynamicCta?.label || 'View All Doctors',
            ariaLabel: cta?.ariaLabel || dynamicCta?.ariaLabel,
        }),
        [cta, dynamicCta],
    );

    /**
     * Execute a coveo search
     * @returns {function} The search action
     */
    const executeSearch = React.useCallback(() => {
        if (SearchActionCreators && SearchAnalyticsActionCreators) {
            return SearchActionCreators.executeSearch(
                SearchAnalyticsActionCreators.logInterfaceLoad(),
            );
        }
        return null;
    }, [SearchActionCreators, SearchAnalyticsActionCreators]);

    /**
     * Trigger a coveo search using the coveoQuery parameter
     * @returns {void}
     */
    const triggerExecuteSearch = React.useCallback(() => {
        if (engine || QueryActionCreators) {
            engine.dispatch(
                QueryActionCreators.updateQuery({
                    enableQuerySyntax: true,
                    q: coveoQuery,
                }),
            );
            engine.dispatch(executeSearch());
        }
    }, [engine, QueryActionCreators, coveoQuery, executeSearch]);

    React.useEffect(() => {
        if (hasCoveoQuery) {
            // Trigger coveo provider search
            triggerExecuteSearch();
        }
    }, [hasCoveoQuery, triggerExecuteSearch]);

    React.useEffect(() => {
        if (hasCoveoQuery && controllerResultsList) {
            /**
             * Subscribe to the results list
             * @returns {function} The unsubscribe function
             */
            const unsubscribe = controllerResultsList.subscribe(() => {
                const { results } = controllerResultsList.state;
                if (Array.isArray(results)) {
                    let slicedResults;

                    setShowCta(results?.length > 3);

                    if (isDynamic) {
                        slicedResults = results?.slice(0, 3);
                    } else {
                        // If there are more than 3 results and geolocation is available and randomized is false, show the first 3;
                        // Otherwise, randomize the results and then show the first 3
                        slicedResults =
                            results && results.length > 3
                                ? !randomized && localStorageGeolocation
                                    ? results.slice(0, 3)
                                    : results
                                          .slice()
                                          .sort(() => 0.5 - Math.random())
                                          .slice(0, 3)
                                : results;
                    }

                    const parsedResults = Array.isArray(slicedResults)
                        ? slicedResults.map((result) => {
                              const interactiveResultController = engine
                                  ? buildInteractiveResult(engine, {
                                        options: { result: result },
                                    })
                                  : null;

                              const { title, clickUri, raw } = result;
                              const {
                                  permanentid,
                                  acceptingnewpatients,
                                  onlinebookingavailable,
                                  primaryspecialty,
                                  specialties,
                                  imageurl,
                                  overallrating,
                                  locationcitystate,
                                  locationcity,
                                  locationstate,
                                  locationphone,
                                  hospitalaffiliations,
                                  latitude,
                                  longitude,
                              } = raw;

                              let image;
                              if (imageurl) {
                                  image = {
                                      defaultImgSrc: imageurl,
                                  };
                              }

                              let location = [];
                              if (locationcitystate) {
                                  location = locationcitystate;
                              } else if (
                                  locationcity &&
                                  locationstate &&
                                  Array.isArray(locationcity)
                              ) {
                                  for (let [index, city] of locationcity.entries()) {
                                      const state = locationstate[index];
                                      location.push(`${city}${state ? ', ' + state : ''}`);
                                  }
                              }

                              let hospitalAffiliation;
                              if (
                                  hospitalaffiliations &&
                                  hospitalaffiliations.includes('Atlantic Medical Group')
                              ) {
                                  hospitalAffiliation = 'Atlantic Medical Group';
                              }

                              const link = {
                                  href: new URL(clickUri).pathname,
                                  target: '_self',
                                  csr: true,
                                  ariaLabel: `Browse to ${title}'s profile`,
                              };

                              const availability = {
                                  phoneNumber:
                                      locationphone && Array.isArray(locationphone)
                                          ? locationphone[0]
                                          : null,
                                  schedulingCta: {
                                      href: `${new URL(clickUri).pathname}#scheduling`,
                                      target: '_self',
                                      csr: true,
                                      ariaLabel: `Browse to ${title}'s times & schedule`,
                                  },
                              };

                              const item = {
                                  id: permanentid,
                                  title: title.replace(',', ', '),
                                  link,
                                  image,
                                  acceptingNewPatients: acceptingnewpatients === 'True',
                                  primarySpecialty: primaryspecialty,
                                  specialties: specialties,
                                  hospitalAffiliation,
                                  location,
                                  averageRating: overallrating,
                                  position: {
                                      lat: latitude,
                                      lng: longitude,
                                  },
                                  onlineBookingAvailable: onlinebookingavailable === 'True',
                                  availability,
                                  coveoAnalyticsCallbacks: interactiveResultController
                                      ? {
                                            cancelPendingSelect: () =>
                                                interactiveResultController.cancelPendingSelect(),
                                            onClick: () => interactiveResultController.select(),
                                            onContextMenu: () =>
                                                interactiveResultController.select(),
                                            onMouseDown: () => interactiveResultController.select(),
                                            onMouseUp: () => interactiveResultController.select(),
                                            onTouchStart: () =>
                                                interactiveResultController.beginDelayedSelect(),
                                            onTouchEnd: () =>
                                                interactiveResultController.cancelPendingSelect(),
                                        }
                                      : undefined,
                              };

                              return item;
                          })
                        : [];

                    // Set the parsed providers
                    setProviders(parsedResults || []);
                }
            });

            return () => {
                // Unsubscribe from the results list
                unsubscribe();
            };
        }
    }, [
        controllerResultsList,
        hasCoveoQuery,
        pageSize,
        localStorageGeolocation,
        randomized,
        isDynamic,
        engine,
    ]);

    return (
        <ProviderCarouselUI
            title={title}
            htmlId={htmlId}
            cta={consolidatedCta}
            ctaPreText={ctaPreText}
            showCta={showCta}
            providers={providers}
        />
    );
};

ProviderCarousel.propTypes = {
    /**
     * component id
     */
    componentId: PropTypes.string,
    /**
     * html id for deep link
     */
    htmlId: PropTypes.string,
    /**
     * title of the component
     */
    title: PropTypes.string.isRequired,
    /**
     * whether the cards are randomized in ordering
     */
    randomized: PropTypes.bool,
    /**
     * CTA Pre-text
     */
    ctaPreText: PropTypes.string,
    /**
     * CTA to overwrite dynamically generated CTA
     */
    cta: PropTypes.shape(Button.propTypes),
    /**
     * dynamically generated CTA. It parameterizes the selected category attribute and appends it to the FAD search URL
     */
    dynamicCta: PropTypes.shape(Button.propTypes),
    /**
     * coveo configuration object
     */
    coveo: PropTypes.shape({
        organizationId: PropTypes.string,
        accessToken: PropTypes.string,
        platformUrl: PropTypes.string,
        renewAccessTokenUrl: PropTypes.string,
    }),
    /**
     * coveo query
     */
    coveoQuery: PropTypes.string,
};

export default ProviderCarousel;
